import { useAuth0 } from "@auth0/auth0-react";
import { Box, CircularProgress } from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import React from "react";

import { getWieldyOrgId, USE_AUTH } from "../../api/apiConfig";
import type QueryClient from "../../api/query/queryClient";
import {
  type BackendFilters,
  getPersistedBackendFilters,
} from "../../utils/backendFilters";
import useDevMode from "../../utils/devmode";
import { practiceSelectionKey } from "../../utils/utils";
import {
  loadTableState,
  type PaginationModel,
  type SortModel,
  useServerFilterModel,
} from "../DataTable";
import InsuranceArListView, {
  INSURANCE_AR_PERSIST_KEY,
  type InsuranceArTabValue,
} from "./InsuranceArListView";
import InsuranceArUploadDialog from "./InsuranceArUploadDialog";

const DEFAULT_PAGE_SIZE = 10;

interface InsuranceArQuery {
  tab: InsuranceArTabValue;
  page: number;
  pageSize: number;
  sortModel: SortModel;
  filters: BackendFilters;
}

interface InsuranceArDashboardProps {
  queryClient: QueryClient;
  selectedPractices: PracticeMessage[];
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "20rem",
        color: "#475467",
      }}
    >
      {children}
    </Box>
  );
}

export default function InsuranceArDashboard({
  queryClient,
  selectedPractices,
  updateSnackBar,
  practices,
  supportedSources,
}: InsuranceArDashboardProps) {
  const devMode = useDevMode();
  const { user } = useAuth0();
  const canUploadReport = !USE_AUTH || user?.org_id === getWieldyOrgId();
  const selectedPracticesKey = practiceSelectionKey(selectedPractices);
  const [overview, setOverview] =
    React.useState<InsuranceArOverviewMessage | null>(null);
  const [entries, setEntries] = React.useState<InsuranceArEntryMessage[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [actionOptions, setActionOptions] =
    React.useState<InsuranceArActionOptionsMessage | null>(null);
  const [query, setQuery] = React.useState<InsuranceArQuery>(() => ({
    tab: "needswork",
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortModel:
      loadTableState(INSURANCE_AR_PERSIST_KEY)?.sorting?.sortModel ?? [],
    filters: getPersistedBackendFilters(INSURANCE_AR_PERSIST_KEY),
  }));
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding selectedPractices.map or dropping selectedPracticesKey would change when this runs
  const practiceIds = React.useMemo(
    () => selectedPractices.map((practice) => practice.wieldyId),
    [selectedPracticesKey],
  );

  const pmsSources = React.useMemo(
    () => supportedSources.filter((s) => s.sourceType === "PMS"),
    [supportedSources],
  );

  React.useEffect(() => {
    const controller = new AbortController();
    queryClient
      .getInsuranceArActionOptions(controller.signal)
      .then(setActionOptions)
      .catch((err) => {
        if (controller.signal.aborted) return;
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to load insurance A/R action options:", err);
      });
    return () => controller.abort();
  }, [queryClient]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping selectedPracticesKey would change when this runs
  React.useEffect(() => {
    setQuery((current) =>
      current.page === 1 ? current : { ...current, page: 1 },
    );
  }, [selectedPracticesKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping refreshKey would change when this runs
  React.useEffect(() => {
    const controller = new AbortController();
    queryClient
      .getInsuranceArOverview(practiceIds, controller.signal)
      .then(setOverview)
      .catch((err) => {
        if (controller.signal.aborted) return;
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to load the insurance A/R overview:", err);
        setError("Failed to load the insurance A/R report.");
      });
    return () => controller.abort();
  }, [queryClient, practiceIds, refreshKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping refreshKey would change when this runs
  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    queryClient
      .searchInsuranceAr({
        practiceIds,
        tab: query.tab,
        page: query.page,
        pageSize: query.pageSize,
        sortField: query.sortModel[0]?.field ?? null,
        sortDirection: query.sortModel[0]?.sort ?? "desc",
        additionalFilters: query.filters,
        signal: controller.signal,
      })
      .then((response) => {
        setEntries(response.data);
        setRowCount(response.total);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        // biome-ignore lint/suspicious/noConsole: intentional browser console output
        console.error("Failed to load insurance A/R entries:", err);
        setError("Failed to load the insurance A/R report.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [queryClient, practiceIds, query, refreshKey]);

  const fetchAllEntries = React.useCallback(
    () =>
      queryClient
        .searchInsuranceAr({
          practiceIds,
          tab: query.tab,
          page: 1,
          pageSize: query.pageSize,
          sortField: query.sortModel[0]?.field ?? null,
          sortDirection: query.sortModel[0]?.sort ?? "desc",
          additionalFilters: query.filters,
          exportMode: true,
        })
        .then((response) => response.data),
    [queryClient, practiceIds, query],
  );

  const onTabChange = (tab: InsuranceArTabValue) =>
    setQuery((current) => ({ ...current, tab, page: 1 }));

  const onPaginationModelChange = (model: PaginationModel) =>
    setQuery((current) => ({
      ...current,
      pageSize: model.pageSize,
      page: model.pageSize === current.pageSize ? model.page + 1 : 1,
    }));

  const onSortModelChange = (sortModel: SortModel) =>
    setQuery((current) => ({ ...current, sortModel, page: 1 }));

  const onFiltersChange = React.useCallback(
    (filters: BackendFilters) =>
      setQuery((current) =>
        JSON.stringify(current.filters) === JSON.stringify(filters)
          ? current
          : { ...current, filters, page: 1 },
      ),
    [],
  );

  const { filterModel, onFilterModelChange } = useServerFilterModel({
    persistKey: INSURANCE_AR_PERSIST_KEY,
    onFiltersChange,
  });

  const header = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <h1>Insurance A/R</h1>
      {canUploadReport && (
        <InsuranceArUploadDialog
          queryClient={queryClient}
          practices={practices}
          selectedPractices={practiceIds}
          pmsSources={pmsSources}
          updateSnackBar={updateSnackBar}
          onUploaded={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </Box>
  );

  let body: React.ReactNode;
  if (error) {
    body = <CenteredMessage>{error}</CenteredMessage>;
  } else if (!overview || !actionOptions) {
    body = (
      <CenteredMessage>
        <CircularProgress />
      </CenteredMessage>
    );
  } else if (overview.reports.every((report) => !report.reportId)) {
    body = (
      <CenteredMessage>
        No insurance A/R report has been processed yet.
      </CenteredMessage>
    );
  } else {
    body = (
      <InsuranceArListView
        overview={overview}
        entries={entries}
        actionOptions={actionOptions}
        devMode={devMode}
        loading={loading}
        queryClient={queryClient}
        updateSnackBar={updateSnackBar}
        practices={practices}
        supportedSources={supportedSources}
        selectedTab={query.tab}
        onTabChange={onTabChange}
        pagination={{
          page: query.page,
          pageSize: query.pageSize,
          rowCount,
        }}
        onPaginationModelChange={onPaginationModelChange}
        sortModel={query.sortModel}
        onSortModelChange={onSortModelChange}
        filterModel={filterModel}
        onFilterModelChange={onFilterModelChange}
        fetchAllEntries={fetchAllEntries}
        onRefresh={() => setRefreshKey((k) => k + 1)}
      />
    );
  }

  return (
    <>
      {header}
      {body}
    </>
  );
}
